import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge, Empty } from "@/components/admin/Ui";
import { CalendarBlank, MapPin, VideoCamera, Users, Plus, Trash, Eye, Sparkle, X } from "@phosphor-icons/react";

const KIND_TONE = { annual: "ember", quarterly_regional: "amber", virtual: "neutral" };
const FORMAT_ICON = { in_person: MapPin, teams: VideoCamera, zoom: VideoCamera, hybrid: Users };

const blank = () => ({
  title: "", kind: "annual", format: "in_person",
  starts_at: "", ends_at: "",
  city: "", country: "", venue: "", region: "",
  agenda: "", description: "", join_url: "", capacity: 0, published: false,
});

export default function Meetings() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invitation, setInvitation] = useState(null); // { meeting, draft, context }

  const load = async () => {
    const { data } = await api.get("/meetings");
    setList(data.meetings || []);
  };
  useEffect(() => { load(); }, []);

  const draftInvitation = async (meetingId) => {
    setBusy(true);
    setInvitation({ loading: true, meetingId });
    try {
      const { data } = await api.post(`/meetings/${meetingId}/invite`);
      setInvitation({ ...data });
      toast.success("Invitation drafted by AI");
    } catch (e) {
      setInvitation(null);
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!editing.title || !editing.starts_at) {
      toast.error("Title and start date required");
      return;
    }
    setBusy(true);
    try {
      if (editing.id) {
        await api.patch(`/meetings/${editing.id}`, editing);
      } else {
        await api.post("/meetings", editing);
      }
      toast.success("Meeting saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this meeting?")) return;
    await api.delete(`/meetings/${id}`);
    await load();
  };

  const togglePublish = async (m) => {
    await api.patch(`/meetings/${m.id}`, { published: !m.published });
    await load();
  };

  return (
    <div data-testid="admin-meetings-page">
      <PageHeader title="Network meetings" subtitle="Annual + Quarterly · Regional" action={
        <button onClick={() => setEditing(blank())} data-testid="meeting-new-button" className="btn-primary">
          <Plus size={14} /> New meeting
        </button>
      } />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className={editing ? "lg:col-span-7" : "lg:col-span-12"}>
          {list.length === 0 ? <Empty label="No meetings scheduled. Create one — the annual is the keystone." /> : (
            <div className="bg-white border border-black/10 overflow-x-auto">
              <table className="w-full text-sm row-alt" data-testid="meetings-table">
                <thead className="border-b border-black/10 text-left text-navy/60">
                  <tr className="text-[10px] uppercase tracking-widest">
                    <th className="px-4 py-3">Meeting</th>
                    <th className="px-4 py-3">Kind</th>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Where</th>
                    <th className="px-4 py-3">RSVPs</th>
                    <th className="px-4 py-3">Published</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((m) => {
                    const FmtIcon = FORMAT_ICON[m.format] || CalendarBlank;
                    return (
                      <tr key={m.id} className="border-b border-black/5" data-testid={`meeting-row-${m.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-navy">{m.title}</div>
                          <div className="text-[11px] text-navy/50">{m.region || "—"}</div>
                        </td>
                        <td className="px-4 py-3"><Badge tone={KIND_TONE[m.kind] || "neutral"}>{m.kind?.replace("_", " ")}</Badge></td>
                        <td className="px-4 py-3 text-navy/80 text-xs">
                          {m.starts_at ? new Date(m.starts_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-4 py-3 text-navy/80 text-xs">
                          <div className="flex items-center gap-1.5"><FmtIcon size={12} /> {m.city ? `${m.city}, ${m.country}` : (m.format === "teams" ? "Teams" : m.format === "zoom" ? "Zoom" : "—")}</div>
                          {m.venue && <div className="text-[10px] text-navy/50">{m.venue}</div>}
                        </td>
                        <td className="px-4 py-3 text-navy/80">{m.rsvps?.length || 0}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => togglePublish(m)} data-testid={`meeting-publish-${m.id}`} className="text-xs uppercase tracking-widest hover:text-ember">
                            {m.published ? <Badge tone="green">Published</Badge> : <Badge tone="amber">Draft</Badge>}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => draftInvitation(m.id)} data-testid={`meeting-invite-${m.id}`} className="text-xs uppercase tracking-widest text-navy hover:text-ember mr-3">
                            <Sparkle size={14} className="inline" /> Draft invite
                          </button>
                          <button onClick={() => setEditing(m)} data-testid={`meeting-edit-${m.id}`} className="text-xs uppercase tracking-widest text-navy hover:text-ember mr-3">
                            <Eye size={14} className="inline" /> Edit
                          </button>
                          <button onClick={() => del(m.id)} data-testid={`meeting-delete-${m.id}`} className="text-xs uppercase tracking-widest text-red-600 hover:text-red-700">
                            <Trash size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {editing && (
          <aside className="lg:col-span-5 bg-white border border-black/10 p-6 self-start" data-testid="meeting-editor">
            <div className="overline mb-4">{editing.id ? "Edit meeting" : "New meeting"}</div>
            <div className="space-y-3">
              <Field label="Title">
                <input data-testid="meeting-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={input} placeholder="EPAS Annual Meet 2026" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Kind">
                  <select data-testid="meeting-kind" value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} className={input}>
                    <option value="annual">Annual (in-person)</option>
                    <option value="quarterly_regional">Quarterly regional</option>
                    <option value="virtual">Virtual / town hall</option>
                  </select>
                </Field>
                <Field label="Format">
                  <select data-testid="meeting-format" value={editing.format} onChange={(e) => setEditing({ ...editing, format: e.target.value })} className={input}>
                    <option value="in_person">In person</option>
                    <option value="teams">Microsoft Teams</option>
                    <option value="zoom">Zoom</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Starts at">
                  <input data-testid="meeting-starts" type="datetime-local" value={editing.starts_at?.slice(0, 16) || ""} onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })} className={input} />
                </Field>
                <Field label="Ends at">
                  <input data-testid="meeting-ends" type="datetime-local" value={editing.ends_at?.slice(0, 16) || ""} onChange={(e) => setEditing({ ...editing, ends_at: e.target.value })} className={input} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="City">
                  <input data-testid="meeting-city" value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className={input} placeholder="Koper" />
                </Field>
                <Field label="Country">
                  <input data-testid="meeting-country" value={editing.country} onChange={(e) => setEditing({ ...editing, country: e.target.value })} className={input} placeholder="Slovenia" />
                </Field>
              </div>
              <Field label="Venue">
                <input data-testid="meeting-venue" value={editing.venue} onChange={(e) => setEditing({ ...editing, venue: e.target.value })} className={input} placeholder="Four Seasons Hotel" />
              </Field>
              <Field label="Region (for quarterly)">
                <input data-testid="meeting-region" value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} className={input} placeholder="Adriatic & CEE" />
              </Field>
              <Field label="Join URL (Teams / Zoom)">
                <input data-testid="meeting-join" value={editing.join_url} onChange={(e) => setEditing({ ...editing, join_url: e.target.value })} className={input} placeholder="https://teams.microsoft.com/..." />
              </Field>
              <Field label="Agenda">
                <textarea data-testid="meeting-agenda" rows={4} value={editing.agenda} onChange={(e) => setEditing({ ...editing, agenda: e.target.value })} className={input} placeholder="Day 1 — Welcome dinner&#10;Day 2 — Corridor sessions&#10;Day 3 — One-to-one meetings&#10;Day 4 — Departure" />
              </Field>
              <Field label="Description">
                <textarea data-testid="meeting-description" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={input} />
              </Field>
              <label className="flex items-center gap-2 text-sm text-navy/70 pt-2">
                <input data-testid="meeting-published" type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
                Publish to member portal
              </label>
            </div>
            <div className="mt-5 flex items-center gap-3 pt-4 border-t border-black/10">
              <button onClick={save} disabled={busy} data-testid="meeting-save-button" className="btn-primary">{busy ? "Saving…" : "Save"}</button>
              <button onClick={() => setEditing(null)} data-testid="meeting-cancel-button" className="btn-secondary">Cancel</button>
            </div>
          </aside>
        )}
      </div>
      {/* Invitation modal */}
      {invitation && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" data-testid="meeting-invitation-modal">
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-black/10 shadow-2xl">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="duotone" className="text-ember" />
                <div className="overline">AI-drafted invitation</div>
              </div>
              <button onClick={() => setInvitation(null)} className="text-navy/50 hover:text-navy" data-testid="meeting-invitation-close">
                <X size={20} />
              </button>
            </div>
            {invitation.loading ? (
              <div className="p-10 text-center text-navy/60">Drafting invitation — GPT-5-mini is composing…</div>
            ) : (
              <div className="p-6 space-y-4">
                <div>
                  <div className="overline mb-2">Subject</div>
                  <div className="font-display font-bold text-lg text-navy" data-testid="invitation-subject">{invitation.draft?.subject}</div>
                </div>
                <div>
                  <div className="overline mb-2">Body</div>
                  <textarea
                    readOnly
                    value={invitation.draft?.body || invitation.draft?.email_body}
                    className="w-full border border-black/15 px-3 py-2 text-sm h-72 font-mono"
                    data-testid="invitation-body"
                  />
                </div>
                {invitation.draft?.personalization_notes && (
                  <div className="text-xs text-navy/60 bg-navy/5 border border-navy/10 p-3">
                    <span className="font-semibold text-navy">Notes: </span>{invitation.draft.personalization_notes}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-navy/40 border-t border-black/10 pt-3">
                  Context used → recent joiners: {invitation.context?.recent_members_count} · total members: {invitation.context?.member_count} · first edition: {invitation.context?.is_first_edition ? "yes" : "no"}
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => { navigator.clipboard.writeText(`Subject: ${invitation.draft?.subject}\n\n${invitation.draft?.email_body || invitation.draft?.body}`); toast.success("Copied to clipboard"); }}
                    data-testid="invitation-copy-button"
                    className="btn-primary"
                  >Copy invitation</button>
                  <button onClick={() => setInvitation(null)} className="btn-secondary" data-testid="invitation-dismiss-button">Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const input = "w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy";
const Field = ({ label, children }) => (
  <div><label className="overline mb-2 block">{label}</label>{children}</div>
);
